import ReelGroup from "./ReelGroup";
import GameEvents from "../const/GameEvents";
import SpinModel from "../model/SpinModel";
import { Tween } from "./../Tween";
import ReelCascadeArrangeBehaviour from "../spinners/ReelCascadeArrangeBehaviour";
import GameModel from "../model/GameModel";
import { ISpinificationData, IWinData, OverlayType } from "../server/data/ISpinData";
import SceneView from "./SceneView";
import App from "../App";
import { AnimatedSprite } from "pixi.js";
import ConsoleUtil from "../utils/ConsoleUtil";
import ReelSymbol from "./Symbol";
import Configs from "../../../src/vendorConfigs/Configs";
import SymbolSpineAnimation from "./SymbolSpineAnimation";
import PromiseHelpers from "../utils/PromiseHelpers";
import ReelCascadeBehaviour from "../spinners/ReelCascadeBehaviour";
import SoundController from "../utils/SoundController";
import SoundVars from "../const/SoundVars";

class CascadeReelGroup extends ReelGroup {
    public spinners: ReelCascadeBehaviour[];
    public popAnimationSprites: AnimatedSprite[] = [];
    public poppedSymbols: ReelSymbol[] = [];
    public isRespin: boolean = false;
    public reelsCascading: number = 0;
    public lastRemovedSymbols: boolean[];
    protected inCascade = false;

    constructor() {
        super();
    }

    public create(
        scene: SceneView,
        symbolData?: any[][],
        groupConfig?: {
            winLineLayer?: string;
            celеbrationLayer?: string;
            winSymbolsLayer?: string;
            reelExtraSymbols?: number;
        }
    ) {
        let noExtraConfig = {
            reelExtraSymbols: 0,
        };

        super.create(scene, symbolData, groupConfig);
    }

    /**
     *
     * @param delay Delay between each reel to start in ms (1000 ms = 1s)
     * @param easeFunc
     */
    public spin(delay: number = 0, easeFunc?: (number) => number) {
        // To avoid possible issues
        Tween.killDelayedCallsTo(this.stopReels);
        if (
            delay <= 0 &&
            Configs?.reels.cascadeProps?.mainSpinProps?.spinReelDelay
        ) {
            if (GameModel.turboSpin) {
                delay =
                    Configs.reels.cascadeProps.mainSpinProps
                        .spinReelDelayInTurboMode;
            } else {
                delay = Configs.reels.cascadeProps.mainSpinProps.spinReelDelay;
            }
        }
        // Clean props
        this.clearProps();
        // Start the spinning
        let totalDelay = 0;
        this.spinTime = Date.now();
        this.spinners.forEach((spinner, reelIdx) => {
            totalDelay = delay * reelIdx;
            spinner.animationStartDelay = totalDelay;
            this.reelsSpinning++;
            this.reelsCascading++;
            this.reels[reelIdx].on(
                GameEvents.CASCADE_STOPPED,
                this.onReelCascade,
                this
            );
        });

        this.spinners.forEach((spinner, reelIdx) => {
            spinner.spin(easeFunc);
        });

        Tween.delayedCall(
            this.getDelayToSendReelsStartedEvent(totalDelay),
            () => {
                this.emit(GameEvents.ALL_REELS_STARTED);
            }
        );

        SoundController.playSound(SoundVars.EFFECT_REEL_START);
    }

    protected clearProps(): void {
        this.hasAnticipation = false;
        this.spinDelayedCalls = [];
        this.reelsSpinning = 0;
        this.reelsCascading = 0;
        this.isMinSpinTimeComplete = false;
        this.stopSymbolsAvailable = false;
        this.stoppingStarted = false;
        this.stopDelayedCalls = [];
        SpinModel.stopImmediately = false;
    }
    /**
     *
     * @param delay Value must be in ms (1000ms = 1s)
     * @returns void
     */
    public cascadeFillRemaining(
        delay = 0,
        spinificationData?: ISpinificationData
    ): void {
        this.stopSymbolsAvailable = true;
        this.spinificationData = spinificationData;
        this.stopDelay = delay;

        if (this.stoppingStarted) {
            return;
        }
        this.stoppingStarted = true;
        let symbols = SpinModel.getCurrentFinalSymbols().get(
            SpinModel.reelSetIndex
        );

        // Loop through the reels and stop each
        this.spinners.forEach((spinnerBehavior, reelIdx) => {
            if (this.isRespin && !this.lastRemovedSymbols[reelIdx]) {
                return;
            }

            const reel = this.reels[reelIdx];
            reel.once(
                GameEvents.REEL_STOPPED,
                () => this.onReelStopped(reelIdx),
                this
            );

            let anticipationData =
                SpinModel.getCurrentSpinProperty("spinification");

            if (
                anticipationData &&
                anticipationData.spinificationIndex <= reelIdx &&
                anticipationData.spinificationEndIndex >= reelIdx
            ) {
                ConsoleUtil.log(
                    "Anticipate reel -> " + reelIdx,
                    anticipationData
                );
                this.hasAnticipation = true;
                spinnerBehavior.activateAnticipation();
                delay += reelIdx * App.config.reelSpinificationDuration;
            } else {
                if (reelIdx !== 0) {
                    if (GameModel.turboSpin) {
                        delay +=
                            Configs.reels.cascadeProps.fillRemainingProps
                                .reelDelayInTurboMode;
                    } else {
                        delay +=
                            Configs.reels.cascadeProps.fillRemainingProps
                                .reelDelay;
                    }
                }
            }

            spinnerBehavior.animationStartDelay = delay;

            spinnerBehavior.fillRemaining(
                symbols[reelIdx],
                this.hasAnticipation
            );
        });

        this.sortSymbols();

        // Notify that reel stopping has started
        this.dispatcher.emit(GameEvents.REEL_STOP_STARTED);

        // End the cascade flag
        this.inCascade = false;
    }
    private getDelayToSendReelsStartedEvent(
        totalReelsDelayToStart: number = 0
    ): number {
        // delay in ms
        let delay = totalReelsDelayToStart;
        if (GameModel.turboSpin) {
            delay +=
                Configs.reels.cascadeProps.mainSpinProps
                    .spinSymbolAnimTimeInTurboMode;
        } else {
            delay +=
                Configs.reels.cascadeProps.mainSpinProps.spinSymbolAnimTime;
        }

        // return delay in seconds
        return delay / 1000;
    }

    /**
     * @deprecated
     */
    public cascade() {
        this.inCascade = true;

        this.spin();
    }
    public fillExplodes() {
        this.inCascade = true;
        let delay = 0;
        // To avoid possible issues
        Tween.killDelayedCallsTo(this.stopReels);
        if (
            Configs?.reels?.cascadeProps?.mainSpinProps?.spinReelDelay
        ) {
            if (GameModel.turboSpin) {
                delay =
                    Configs.reels.cascadeProps.mainSpinProps
                        .spinReelDelayInTurboMode;
            } else {
                delay = Configs.reels.cascadeProps.mainSpinProps.spinReelDelay;
            }
        }
        // Clean props
        this.clearProps();
        // Start the spinning
        let totalDelay = 0;
        this.spinTime = Date.now();
        this.spinners.forEach((spinner, reelIdx) => {
            totalDelay = delay * reelIdx;
            spinner.animationStartDelay = totalDelay;
            this.reelsSpinning++;
            this.reelsCascading++;
            this.reels[reelIdx].on(
                GameEvents.CASCADE_STOPPED,
                this.onReelCascade,
                this
            );
        });

        this.spinners.forEach((spinner, reelIdx) => {
            spinner.fillExplodes(this.getFillExplodeProps(reelIdx));
        });
        this.sortSymbols();
    }

    protected getFillExplodeProps(reelIdx: number): {explodesMap: any[], explodedItems: boolean[], lockedOverlay: number[]} {
        const explodesMap = SpinModel.getCurrentCascadeExplodeMap();
        const lockOverlay = SpinModel?.getOverlay(OverlayType.Locked);
        const lockedOverlay = lockOverlay?.symbolPosition[reelIdx] || [];
        const explodedItems =
        explodesMap.length > 0 ? explodesMap[reelIdx] : [];

        return ({
            explodesMap,
            explodedItems,
            lockedOverlay
        })
    }


    public onReelCascade(reelIdx: number) {
        this.reelsCascading--;
        const reel = this.reels[reelIdx];
        reel.off(GameEvents.CASCADE_STOPPED, this.onReelCascade, this);
        // I'm not sure this is needed !!!
        if (this.reelsCascading == 0) {
            let spinTime = Date.now();
            let reelDelay = this.config.minSpinTime;
            if (GameModel.turboSpin) {
                reelDelay = this.config.quickSpinTime;
            }

            if (this.inCascade) {
                reelDelay = 0.5;
            }

            let remainingTime = this.spinTime + reelDelay * 1000 - spinTime;

            if (!this.stoppingStarted && this.reelsCascading == 0) {
                if (SpinModel.stopImmediately || remainingTime <= 0) {
                    this.allowStop();
                } else {
                    this.addDelayedCall(
                        remainingTime / 1000,
                        this.allowStop,
                        [],
                        this
                    );
                }
            }
        }
    }
    protected setUpPopSymPromise(
        reel: number,
        cell: number,
        animName?: string
    ): Promise<SymbolSpineAnimation> {
        return new Promise<SymbolSpineAnimation>((res) => {
            let symbol = this.getSymbolAt(reel, cell);
            symbol.playAnimation(animName || "ex_feature").then(() => {
                res(symbol.spineAnim);
            });
        });
    }
    /**
     * @param winningSymbols The matrix of reel symbols, if value in matrix is true, symbol must be explode
     * @returns Array with animation promises
     */
    protected loopThroughWinningPositions(
        winningPositions: boolean[][]
    ): Promise<SymbolSpineAnimation>[] {
        let expSymPromises: Promise<SymbolSpineAnimation>[] = [];
        if (winningPositions && winningPositions.length > 0) {
            for (let i = 0; i < winningPositions.length; i++) {
                for (let j = 0; j < winningPositions[i].length; j++) {
                    if (winningPositions[i][j]) {
                        expSymPromises.push(this.setUpPopSymPromise(i, j));
                    }
                }
            }
        }

        return expSymPromises;
    }

    /**
     * @param winningSymbols The matrix of reel symbols, if value in matrix is true, symbol must be explode
     */
    protected popSymbolsWithSpine(winningSymbols?: boolean[][]): void {
        if (winningSymbols && winningSymbols.length > 0) {
            let expSymPromises: Promise<SymbolSpineAnimation>[] =
                this.loopThroughWinningPositions(winningSymbols);
            if (expSymPromises.length > 0) {
                PromiseHelpers.combineParallel(expSymPromises).then(() => {
                    this.dispatcher.emit(GameEvents.ALL_SYMBOLS_POPPED);
                });
            } else {
                this.dispatcher.emit(GameEvents.ALL_SYMBOLS_POPPED);
            }
        }
    }
    /**
     * @deprecated
     * @param winningSymbols The matrix of reel symbols, if value in matrix is true, symbol must be explode
     */
    protected popSymbolsWithSpriteAnim(winningSymbols?: boolean[][]): void {
        const frames = this.assets.generateFrames(
            "explode" + "_",
            3,
            GameModel.gameConfig.animData["explode"].numFrames
        );
        const winningPositions =
            winningSymbols || SpinModel.getCurrentCascadeExplodeMap();
        this.popAnimationSprites = [];
        for (let i = 0; i < winningPositions.length; i++) {
            for (let j = 0; j < winningPositions[i].length; j++) {
                if (winningPositions[i][j]) {
                    const winningSymbol = this.reels[i].symbols[j];
                    this.poppedSymbols.push(winningSymbol);
                    winningSymbol.visible = false;

                    const sprite = new AnimatedSprite(frames);
                    sprite.animationSpeed = GameModel.gameConfig.popAnimSpeed;
                    this.popAnimationSprites.push(sprite);
                    sprite.anchor.set(0.5, 0.5);
                    sprite.loop = false;
                    sprite.position.x = winningSymbol.position.x;
                    sprite.position.y = winningSymbol.position.y;
                    sprite.width = winningSymbol.width * 2;
                    sprite.height = winningSymbol.height * 2;
                    this.symContainer.addChild(sprite);
                    sprite.gotoAndPlay(0);
                }
            }
        }

        if (this.popAnimationSprites.length > 0) {
            this.popAnimationSprites[
                this.popAnimationSprites.length - 1
            ].onComplete = () => {
                this.popAnimationSprites.forEach((anim) => {
                    anim.destroy();
                });
                this.dispatcher.emit(GameEvents.ALL_SYMBOLS_POPPED);
            };
        } else {
            this.dispatcher.emit(GameEvents.ALL_SYMBOLS_POPPED);
        }
    }

    public popSymbols(winningSymbols?: boolean[][]) {
        if (Configs.game.symbolType.spine) {
            this.popSymbolsWithSpine(winningSymbols);
        } else {
            this.popSymbolsWithSpriteAnim(winningSymbols);
        }
    }

    public removePopAnimations() {
        for (let i = 0; i < this.popAnimationSprites.length; i++) {
            this.symContainer.removeChild(this.popAnimationSprites[i]);
        }
        this.popAnimationSprites = [];
    }

    public restorePoppedSymbols() {
        for (let i = 0; i < this.poppedSymbols.length; i++) {
            this.poppedSymbols[i].visible = true;
        }
        this.poppedSymbols = [];
    }
    public stop(
        stopData: any[][],
        delay = 0,
        spinificationData?: ISpinificationData
    ) {
        this.stopSymbolsAvailable = true;
        this.resultData = stopData;
        this.spinificationData = spinificationData;
        this.stopDelay = delay;
        this.stopReels(delay);
    }

    public showFinalSymbols() {
        // override with empty
    }
    /**
     *
     * @param delay Value must be in ms (1000ms = 1s)
     * @returns void
     */
    protected stopReels(delay = 0): void {
        if (this.stoppingStarted) {
            return;
        }
        this.stoppingStarted = true;
        let symbols = SpinModel.getCurrentFinalSymbols().get(
            SpinModel.reelSetIndex
        );

        // TODO:  If this is a re-spin - get the correct symbols
        //if (this.isRespin) {
        // symbols = SpinModel.getNextGenSymbols();
        //}

        // Loop through the reels and stop each
        this.spinners.forEach((spinnerBehavior, reelIdx) => {
            if (this.isRespin && !this.lastRemovedSymbols[reelIdx]) {
                return;
            }

            const reel = this.reels[reelIdx];
            reel.once(
                GameEvents.REEL_STOPPED,
                () => this.onReelStopped(reelIdx),
                this
            );

            // Put a delay between reel stops - grows for each reel
            // If we have anticipation - longer delay should be used

            let anticipationData =
                SpinModel.getCurrentSpinProperty("spinification");

            if (
                anticipationData &&
                anticipationData.spinificationIndex <= reelIdx &&
                anticipationData.spinificationEndIndex >= reelIdx
            ) {
                ConsoleUtil.log(
                    "Anticipate reel -> " + reelIdx,
                    anticipationData
                );
                this.hasAnticipation = true;
                spinnerBehavior.activateAnticipation();
                delay += reelIdx * App.config.reelSpinificationDuration;
            } else {
                if (reelIdx !== 0) {
                    if (GameModel.turboSpin) {
                        delay +=
                            Configs.reels.cascadeProps.mainSpinProps
                                .stopReelDelayInTurboMode;
                    } else {
                        delay +=
                            Configs.reels.cascadeProps.mainSpinProps
                                .stopReelDelay;
                    }
                }
            }

            spinnerBehavior.animationStartDelay = delay;

            // Then stop the reel
            spinnerBehavior.stop(symbols[reelIdx], this.hasAnticipation);
        });

        // Notify that reel stopping has started
        this.dispatcher.emit(GameEvents.REEL_STOP_STARTED);

        // End the cascade flag
        this.inCascade = false;
    }

    public stopImmediately(e: any) {
        SpinModel.stopImmediately = true;
        for (let reelIdx = 0; reelIdx < this.spinners.length; reelIdx++) {
            const s = this.spinners[reelIdx];
            s.activateQuickStop();
        }

        super.stopImmediately(e);
    }

    public onReelStopped(reelIdx: number) {
        this.reelsSpinning--;
        this.dispatcher.emit(GameEvents.REEL_STOPPED, reelIdx); // used in idleParticlesController
        if (this.reelsSpinning == 0) {
            this.dispatcher.emit(GameEvents.ALL_REELS_STOPPED);
        }
    }

    public displayBonusResult(
        winlinesToShow: IWinData[],
        firstWildIndex,
        wildVersion
    ) {
        winlinesToShow.forEach((winline) => {
            this.highlightWinlineWin(winline, false);
            this.winlinesView.highlightWinLine(winline, false);
        });
        this.on(GameEvents.GROUP_ANIM_COMPLETE, () => {
            this.finishShowingWinlines();
            winlinesToShow.forEach((winline) => this.hideWinlineWin(winline));
        });
    }
}

export default CascadeReelGroup;
